import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { ErrorState, Loading } from "@/components/States";
import { PackReviewContent } from "@/pages/parent/PackReviewContent";

const ACTIVE = ["uploaded", "queued", "processing", "indexing"];

export default function PackReview() {
  const { packId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.get(`/parent/study-packs/${packId}/review`);
      setData(res.data);
    } catch (err) {
      setError(apiError(err, "No pudimos cargar la revision."));
    }
  }, [packId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!data) return;
    const busy = ACTIVE.includes(data.pack.status) || data.documents.some((d) => ACTIVE.includes(d.status));
    if (!busy) return;
    const timer = setInterval(load, 6000);
    return () => clearInterval(timer);
  }, [data, load]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const body = new FormData();
    Array.from(files).forEach((file) => body.append("files", file));
    try {
      const res = await api.post(`/parent/study-packs/${packId}/documents`, body);
      const { accepted, duplicates, rejected } = res.data;
      if (accepted.length) toast.success(`${accepted.length} pagina(s) en proceso. Puedes seguir navegando.`);
      if (duplicates.length) toast.info(`${duplicates.length} pagina(s) ya estaban subidas (no se reprocesan).`);
      rejected.forEach((r) => toast.error(`${r.filename}: ${r.reason}`));
      await load();
    } catch (err) {
      toast.error(apiError(err, "No pudimos subir las imagenes."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const retry = async (documentId) => {
    try {
      await api.post(`/parent/documents/${documentId}/retry`);
      toast.success("Reintentando esa pagina.");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (documentId) => {
    try {
      await api.delete(`/parent/documents/${documentId}`);
      toast.success("Pagina descartada.");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openOriginal = async (doc) => {
    try {
      const response = await api.get(doc.original_url, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(apiError(err, "No pudimos abrir la pagina original."));
    }
  };

  const reprocess = async () => {
    try {
      await api.post(`/parent/study-packs/${packId}/reprocess`);
      toast.success("Reprocesando. El texto ya interpretado se reutiliza.");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading label="Cargando revision del material..." />;

  return (
    <PackReviewContent
      data={data}
      fileRef={fileRef}
      uploading={uploading}
      onUpload={upload}
      onRefresh={load}
      onReprocess={reprocess}
      onOpenOriginal={openOriginal}
      onRetry={retry}
      onRemove={remove}
    />
  );
}
